import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";

export const MISSING_INVOICE_MESSAGE =
  "Are you sure you want to complete without adding the invoice?";

export const hasInvoiceAttachment = ({ uploadInvoice, fileName, uploadInvoiceFileBlobName }) =>
  Boolean(uploadInvoice || fileName?.trim() || uploadInvoiceFileBlobName?.trim());

// Shared by both active Add Details forms so cancellation and repeat clicks behave alike.
export default function useInvoiceCompletion() {
  const pendingRef = useRef(false);
  const mountedRef = useRef(true);
  const dialogOpenRef = useRef(false);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (dialogOpenRef.current) Swal.close();
    };
  }, []);

  const runCompletion = async (action) => {
    if (pendingRef.current || !mountedRef.current) return;
    pendingRef.current = true;
    setIsCompleting(true);
    try {
      await action();
    } finally {
      pendingRef.current = false;
      if (mountedRef.current) setIsCompleting(false);
    }
  };

  const confirmCompletion = async (attachment) => {
    if (!mountedRef.current) return false;
    dialogOpenRef.current = true;
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: hasInvoiceAttachment(attachment)
          ? "Do you want to add the invoice and mark the order as complete?"
          : MISSING_INVOICE_MESSAGE,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
        focusCancel: true,
        // Capture Escape before the parent Add Details modal handles it.
        keydownListenerCapture: true,
        stopKeydownPropagation: true,
      });
      return mountedRef.current && result.isConfirmed === true;
    } finally {
      dialogOpenRef.current = false;
    }
  };

  return { runCompletion, confirmCompletion, isCompleting };
}
